<template>
    <div
        class="scc-ticket-checker-alternative-container"
        :class="`scc-ticket-checker-alternative-container--${backgroundColor}`"
    >
        <div class="scc-ticket-checker-alternative">
            <h1 class="h1 scc-ticket-checker-alternative__title">{{ resolvedTitle }}</h1>
            <template v-if="showTicketChecker">
                <div class="scc-ticket-checker-alternative__checking-container">
                    <template v-if="drawingHasPrizes(publishedDrawing)">
                        <div
                            class="scc-ticket-checker-alternative__results"
                            :class="isShowingResults ? 'scc-ticket-checker-alternative__results--active' : null"
                        >
                            <p v-if="isShowingResults" class="scc-ticket-checker-alternative__subtitle">
                                Trekking van {{ gameName }}
                            </p>
                            <p
                                class="scc-ticket-checker-alternative__label"
                                :class="isShowingResults ? 'scc-ticket-checker-alternative__label--active' : null"
                            >
                                {{ resolvedTicketLabel }}
                            </p>
                            <TicketCheckerEntryAlternative
                                v-for="(ticket, index) in computedTickets"
                                :key="index"
                                ref="checkerEntry"
                                v-model="ticket.value"
                                :drawing="publishedDrawing"
                                :ticketFormat="ticketFormat"
                                :no-prize-text="noPrizeText"
                                :color="backgroundColor"
                                @won="hasWinningTickets = true"
                            ></TicketCheckerEntryAlternative>
                        </div>
                    </template>
                </div>
                <div class="scc-ticket-checker-alternative__checking-container">
                    <template v-if="drawingHasPrizes(publishedParentDrawing)">
                        <div
                            class="scc-ticket-checker-alternative__results"
                            :class="isShowingResults ? 'scc-ticket-checker-alternative__results--active' : null"
                        >
                            <p v-if="isShowingResults" class="scc-ticket-checker-alternative__subtitle">
                                Trekking van {{ parentGameName }}
                            </p>
                            <p
                                v-if="!publishedDrawing || isShowingResults"
                                class="scc-ticket-checker-alternative__label"
                                :class="isShowingResults ? 'scc-ticket-checker-alternative__label--active' : null"
                            >
                                {{ resolvedTicketLabel }}
                            </p>
                            <TicketCheckerEntryAlternative
                                v-for="(ticket, index) in computedTickets"
                                v-show="!publishedDrawing || isShowingResults"
                                :key="'parent ' + index"
                                ref="parentCheckerEntry"
                                v-model="ticket.value"
                                :drawing="publishedParentDrawing"
                                :ticketFormat="ticketFormat"
                                :no-prize-text="noPrizeText"
                                :color="backgroundColor"
                                @won="hasWinningTickets = true"
                            ></TicketCheckerEntryAlternative>
                        </div>
                    </template>
                </div>
                <span v-if="invalidInput" class="mb-2 note">
                    Er moet tenminste 1 cijfer ingevuld worden om een lot te kunnen checken.
                </span>

                <button
                    v-show="tickets.length < max && !isShowingResults"
                    class="scc-ticket-checker-alternative__add-more"
                    @click="addHandler"
                >
                    <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M16.9922 11.7188H13.2812V8.00781C13.2812 7.90039 13.1934 7.8125 13.0859 7.8125H11.9141C11.8066 7.8125 11.7188 7.90039 11.7188 8.00781V11.7188H8.00781C7.90039 11.7188 7.8125 11.8066 7.8125 11.9141V13.0859C7.8125 13.1934 7.90039 13.2812 8.00781 13.2812H11.7188V16.9922C11.7188 17.0996 11.8066 17.1875 11.9141 17.1875H13.0859C13.1934 17.1875 13.2812 17.0996 13.2812 16.9922V13.2812H16.9922C17.0996 13.2812 17.1875 13.1934 17.1875 13.0859V11.9141C17.1875 11.8066 17.0996 11.7188 16.9922 11.7188Z"
                            fill="white"
                        />
                        <path
                            d="M12.5 1.5625C6.45996 1.5625 1.5625 6.45996 1.5625 12.5C1.5625 18.54 6.45996 23.4375 12.5 23.4375C18.54 23.4375 23.4375 18.54 23.4375 12.5C23.4375 6.45996 18.54 1.5625 12.5 1.5625ZM12.5 21.582C7.48535 21.582 3.41797 17.5146 3.41797 12.5C3.41797 7.48535 7.48535 3.41797 12.5 3.41797C17.5146 3.41797 21.582 7.48535 21.582 12.5C21.582 17.5146 17.5146 21.582 12.5 21.582Z"
                            fill="white"
                        />
                    </svg>

                    {{ addButtonText }}
                </button>

                <template v-if="!isShowingResults">
                    <span v-if="text" class="scc-ticket-checker-alternative__note note">
                        {{ text }}
                    </span>

                    <button
                        class="scc-link scc-link--button scc-link--large scc-link--align-center scc-link--primary scc-ticket-checker-alternative__button"
                        @click="checkHandler"
                    >
                        {{ checkButtonText }}
                    </button>
                </template>

                <template v-else>
                    <p v-if="textResult" class="scc-ticket-checker-alternative__note note">
                        {{ textResult }}
                    </p>

                    <button
                        class="scc-link scc-link--button scc-link--large scc-link--align-center scc-link--primary scc-ticket-checker-alternative__button"
                        @click="resetHandler"
                    >
                        {{ checkMoreButtonText }}
                    </button>
                </template>
            </template>

            <template v-else>
                <span class="scc-ticket-checker-alternative__note">
                    Er zijn nog geen trekkingsuitslagen beschikbaar
                </span>
            </template>
        </div>
    </div>
</template>

<script>
import { cloneDeep } from 'lodash';
import './ticket-checker-alternative.scss';
import '../global/scc-link.scss';
import TicketCheckerEntryAlternative from './TicketCheckerEntryAlternative.vue';

export default {
    name: 'TicketCheckerAlternative',
    components: { TicketCheckerEntryAlternative },
    __editor: {
        requiredData: ['game', 'parentGame', 'drawings', 'parentDrawings'],
        fields: {
            backgroundColor: {
                widget: 'select',
                values: ['primary', 'secondary', 'body', 'white'],
            },
            text: {
                widget: 'textarea',
            },
            textWon: {
                widget: 'textarea',
            },
            textLost: {
                widget: 'textarea',
            },
        },
    },
    props: {
        game: {
            type: Object,
            required: true,
        },
        parentGame: {
            type: Object,
            required: true,
        },
        drawings: {
            type: Array,
            required: true,
        },
        parentDrawings: {
            type: Array,
            required: true,
        },
        backgroundColor: {
            type: String,
            required: false,
            default: 'secondary',
            validator: function(val) {
                return ['primary', 'secondary', 'body', 'white'].includes(val);
            },
        },
        max: {
            type: Number,
            default: 10,
        },
        title: {
            type: String,
            default: 'Trekkingsuitslag',
        },
        titleWon: String,
        titleLost: String,
        text: {
            type: String,
            default: undefined,
        },
        textWon: String,
        textLost: String,
        drawingLimit: {
            type: Number,
            default: 5,
        },
        ticketLabel: {
            type: String,
            default: 'Je lotnummer(s)',
        },
        ticketLabelResults: {
            type: String,
            default: 'Lotnummer',
        },
        drawingLabel: {
            type: String,
            default: 'Trekking van',
        },
        addButtonText: {
            type: String,
            default: 'Nog een lot toevoegen',
        },
        checkButtonText: {
            type: String,
            default: 'Nu checken!',
        },
        checkMoreButtonText: {
            type: String,
            default: 'Meer loten checken!',
        },
        noPrizeText: {
            type: String,
            default: 'Helaas, geen prijs',
        },
    },
    data() {
        return {
            tickets: [
                {
                    value: this.ticketPrefix,
                },
            ],
            isShowingResults: false,
            invalidInput: false,
            hasWinningTickets: false,
        };
    },
    computed: {
        parentGameName() {
            return this.parentGame?.name;
        },
        gameName() {
            return this.game?.name;
        },
        showTicketChecker() {
            if (this.drawings.length > 0) return this.publishedDrawing && this.publishedParentDrawing;
            else return this.publishedParentDrawing;
        },
        publishedParentDrawing() {
            const drawings = [...this.parentDrawings];
            return drawings.find(drawing => {
                return drawing.status && drawing.status === 'published';
            });
        },
        publishedDrawing() {
            const drawings = [...this.drawings];
            return drawings.find(drawing => {
                return drawing.status && drawing.status === 'published';
            });
        },
        ticketPrefix() {
            const winningTickets = this.publishedParentDrawing?.winningTickets || this.publishedDrawing?.winningTickets;
            if (winningTickets && winningTickets[0] && winningTickets[0].ticketCode?.includes('VTBL-')) {
                return 'VTBL-';
            }
            return '';
        },
        ticketFormat() {
            const winningTickets = this.publishedParentDrawing?.winningTickets || this.publishedDrawing?.winningTickets;

            if (winningTickets && winningTickets[0]) {
                return {
                    length: winningTickets[0].ticketCode?.length,
                    numeric: !isNaN(winningTickets[0].ticketCode),
                };
            }

            return {};
        },
        textResult() {
            return this.hasWinningTickets ? this.textWon : this.textLost;
        },
        resolvedTitle() {
            if (this.isShowingResults) {
                if (this.hasWinningTickets && this.titleWon) {
                    return this.titleWon;
                } else if (!this.hasWinningTickets && this.titleLost) {
                    return this.titleLost;
                }
            }

            return this.title;
        },
        resolvedTicketLabel() {
            if (this.isShowingResults && this.ticketLabelResults) {
                return this.ticketLabelResults;
            }

            return this.ticketLabel;
        },
        computedTickets() {
            return this.tickets;
        },
    },
    mounted() {
        this.tickets = [{ value: this.ticketPrefix }];
    },
    methods: {
        drawingHasPrizes(drawing) {
            return drawing?.prizes?.length > 0 && drawing?.winningTickets?.length > 0;
        },
        addHandler() {
            this.tickets.push({
                value: this.ticketPrefix,
            });
        },
        checkHandler() {
            this.invalidInput = false;
            this.tickets = cloneDeep(this.computedTickets.filter(ticket => ticket.value && ticket.value !== ''));

            if (this.publishedDrawing) {
                if (this.$refs.checkerEntry[0].userTicket.length < 1) this.invalidInput = true;

                this.$refs.checkerEntry.forEach((entry, i) => {
                    entry.userTicket = this.tickets[i]?.value;
                    this.$refs.parentCheckerEntry[i].userTicket = entry.userTicket;
                });
            } else {
                this.$refs.parentCheckerEntry.forEach((entry, i) => {
                    entry.userTicket = this.tickets[i]?.value;
                });
            }

            if (!this.invalidInput) {
                if (this.publishedDrawing) this.$refs.checkerEntry.forEach(i => i.checkTicket());
                this.$refs.parentCheckerEntry.forEach(i => i.checkTicket());
                this.isShowingResults = true;
            }
        },
        resetHandler() {
            this.tickets = [{ value: '' }];
            this.isShowingResults = false;
            if (this.publishedDrawing) this.$refs.checkerEntry.forEach(i => i.reset());
            this.$refs.parentCheckerEntry.forEach(i => i.reset());
            this.hasWinningTickets = false;
        },
    },
};
</script>
